/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, Subject, Subscription, takeUntil } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import {
  Product,
  User,
  ProductImportFromExistCandidate,
  ProductImportFromExistResult,
  Provider
} from 'src/typings';
import { ExistImportSelectionDialogComponent } from '../exist-import-selection-dialog/exist-import-selection-dialog.component';
import { ProductService } from '../product.service';
import { ProductAiEnrichmentDialogComponent } from './product-ai-enrichment-dialog.component';
import { ProductMinimumStockChange } from '../../procurement/analytics/procurement-minimum-stock.component';
import { AnalyticsPeriod, AnalyticsWarehouseMinimumStock } from '../../procurement/analytics/procurement-analytics.types';
import { analyticsFiltersFromQuery } from '../../procurement/analytics/procurement-analytics.util';
import { AppState } from '../../redux/app.state';
import { groupProductSupplierOffers, ProductSupplierGroup, ProductSupplierOffer } from './product-supplier-groups';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnDestroy {
  set product(prod: Product | undefined) {
    if (!prod) return;
    const preserveEdits = this._product?.id === prod.id;
    this._product = prod;
    if (!preserveEdits) this.expandedSupplierKeys.clear();
    this.rebuildSupplierGroups();
    const values: Record<string, unknown> = {
      description: prod.description,
      description_ua: prod.description_ua,
      brand: prod.brand,
      article: prod.article,
      description_long: prod.description_long,
      description_long_ua: prod.description_long_ua,
      retail: prod.retail,
      fixedMarkupPercent: prod.fixedMarkupPercent ?? null,
      pricingProvider: prod.pricingProvider ?? null,
      prom: prod.prom,
      avtopro: prod.avtopro,
      search_keys: prod.searchKeys,
      stable: prod.stable
    };
    Object.entries(values).forEach(([key, value]) => {
      const control = this.productForm.controls[key];
      // Stock/photo websocket updates must not replace what the operator is typing.
      if (preserveEdits && control.dirty) return;
      control.setValue(value, { emitEvent: false });
    });
    if (!preserveEdits) this.productForm.markAsPristine();
  }

  saving = false;

  importingFromExist = false;

  enrichingWithAi = false;

  loadingProduct = false;

  loadError = '';

  selectedTabIndex = 0;
  salesPeriod: AnalyticsPeriod | null = null;
  mainInfoCollapsed = true;
  minimumStockChanges: Record<number, AnalyticsWarehouseMinimumStock> = {};
  supplierGroups: ProductSupplierGroup[] = [];
  expandedSupplierKeys = new Set<string>();
  private supplierCityId: number | null = null;
  private storeSubscription: Subscription;

  toggleSupplier(group: ProductSupplierGroup) {
    if (this.expandedSupplierKeys.has(group.key)) this.expandedSupplierKeys.delete(group.key);
    else this.expandedSupplierKeys.add(group.key);
  }

  trackSupplier(_index: number, group: ProductSupplierGroup): string {
    return group.key;
  }

  supplierAvailability(offer: ProductSupplierOffer): string {
    if (offer.availability === 'local') return 'В городе';
    if (offer.availability === 'unavailable') return 'Нет в наличии';
    return this.supplierCityId && offer.cityId ? 'Другой город' : 'В наличии';
  }

  private rebuildSupplierGroups() {
    this.supplierCityId = this.resolveSupplierCityId();
    this.supplierGroups = groupProductSupplierOffers(this.product?.providerStock, this.supplierCityId);
    const keys = new Set(this.supplierGroups.map((group) => group.key));
    this.expandedSupplierKeys.forEach((key) => {
      if (!keys.has(key)) this.expandedSupplierKeys.delete(key);
    });
  }

  private resolveSupplierCityId(): number | null {
    const userCityId = Number(this.user?.defaultWarehouse?.city?.id);
    if (Number.isInteger(userCityId) && userCityId > 0) return userCityId;
    // Restored user sessions include the default warehouse ID but can omit its city.
    // The product's stock response contains all our warehouses, including zero stock.
    const warehouseId = Number(this.user?.defaultWarehouse?.id);
    const warehouse = this.product?.stock?.find((row) => Number(row.warehouse?.id) === warehouseId)?.warehouse;
    const cityId = Number(warehouse?.city?.id);
    return Number.isInteger(cityId) && cityId > 0 ? cityId : null;
  }

  toggleMainInfo() {
    this.mainInfoCollapsed = !this.mainInfoCollapsed;
  }

  minimumStockSaved(change: ProductMinimumStockChange) {
    if (change.productId !== this.product?.id) return;
    this.minimumStockChanges = {
      ...this.minimumStockChanges,
      [change.stock.warehouseId]: change.stock
    };
  }

  get product() {
    return this._product;
  }

  get primaryImage() {
    const images = this.product?.images || [];
    return images.find((image) => image.isPrimary === true) || images[0];
  }

  _product: Product | undefined;

  private loadRequestId = 0;

  private activeProductId: number | undefined;

  private readonly cancelProductLoad = new Subject<void>();

  private destroyed = false;

  private routeSubscription: Subscription;

  user: User | undefined;

  productForm: UntypedFormGroup = this.fb.group({
    description: new UntypedFormControl(),
    description_ua: new UntypedFormControl(),
    brand: new UntypedFormControl(),
    article: new UntypedFormControl(),
    description_long: new UntypedFormControl(),
    description_long_ua: new UntypedFormControl(),
    retail: new UntypedFormControl(),
    fixedMarkupPercent: new UntypedFormControl(),
    pricingProvider: new UntypedFormControl(),
    stable: new UntypedFormControl(),
    prom: new UntypedFormControl(),
    avtopro: new UntypedFormControl(),
    search_keys: new FormControl<string>('')
  });

  get brandControl() {
    return this.productForm.controls.brand as UntypedFormControl;
  }

  get pricingProviderControl() {
    return this.productForm.controls.pricingProvider as UntypedFormControl;
  }

  productSubscription: Subscription;

  constructor(
    private productService: ProductService,
    private activateRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private store: Store<AppState>
  ) {
    this.storeSubscription = this.store.subscribe((state) => {
      this.user = state.user.info;
      if (this.supplierCityId !== this.resolveSupplierCityId()) {
        this.rebuildSupplierGroups();
      }
    });
    this.productSubscription = this.productService.productGw.subscribe((newProd) => {
      const find = newProd.find((newProd_) => newProd_.id === this.product?.id);
      if (find) {
        this.product = { ...find };
      }
    });
    this.routeSubscription = combineLatest([
      this.activateRoute.params, this.activateRoute.queryParams
    ]).subscribe(([params, query]) => {
      const filters = analyticsFiltersFromQuery(query);
      this.salesPeriod = query.tab === 'sales'
        ? { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
        : null;
      if (params.id && Number(params.id) !== this.activeProductId) {
        void this.loadProduct(Number(params.id));
      }
      this.selectedTabIndex = query.tab === 'sales' ? 4 : 0;
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.loadRequestId++;
    this.cancelProductLoad.next();
    this.cancelProductLoad.complete();
    this.productSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();
    this.storeSubscription.unsubscribe();
  }

  selectWorkspaceTab(index: number) {
    if (Number.isInteger(index) && index >= 0 && index < 5) {
      this.selectedTabIndex = index;
    }
  }

  updateGalleryProduct(product: Product) {
    if (product.id === this.product?.id) this.product = product;
  }

  retryLoading() {
    if (this.activeProductId && !this.loadingProduct) {
      void this.loadProduct(this.activeProductId);
    }
  }

  async loadProduct(id: number) {
    if (this.destroyed) return;
    id = Number(id);
    const requestId = ++this.loadRequestId;
    this.cancelProductLoad.next();
    if (this.activeProductId !== id) {
      this._product = undefined;
      this.selectedTabIndex = 0;
      this.mainInfoCollapsed = true;
      this.minimumStockChanges = {};
      this.supplierGroups = [];
      this.expandedSupplierKeys.clear();
      this.productForm.reset({}, { emitEvent: false });
    }
    this.activeProductId = id;
    this.loadError = '';
    if (!Number.isInteger(id) || id <= 0) {
      this.loadError = 'Некорректный номер товара';
      this.loadingProduct = false;
      return;
    }
    this.loadingProduct = true;
    try {
      const res = await firstValueFrom(
        this.productService.getOne(id).pipe(takeUntil(this.cancelProductLoad)),
        { defaultValue: undefined }
      );
      if (this.destroyed || requestId !== this.loadRequestId) return;
      if (!res) {
        this.loadError = 'Товар не найден';
        return;
      }
      this.product = res;
    } catch (err) {
      if (this.destroyed || requestId !== this.loadRequestId) return;
      console.error(err);
      this.loadError = 'Не удалось загрузить карточку товара';
      this.notificationService.warning(this.loadError);
    } finally {
      if (requestId === this.loadRequestId) {
        this.loadingProduct = false;
      }
    }
  }

  async save() {
    if (!this.product || this.saving || this.importingFromExist) return;
    const productId = this.product.id;
    const submitted = this.productForm.getRawValue();
    if (!submitted.brand?.id) {
      this.notificationService.warning('Выберите производителя из списка');
      return;
    }
    this.saving = true;
    try {
      const res = await firstValueFrom(
        this.productService.updateProduct({
          id: productId,
          description: submitted.description,
          description_ua: submitted.description_ua,
          description_long: submitted.description_long,
          description_long_ua: submitted.description_long_ua,
          prom: submitted.prom,
          avtopro: submitted.avtopro,
          brand_id: submitted.brand.id,
          retail: submitted.retail,
          fixedMarkupPercent: this.normalizeFixedMarkupPercent(
            submitted.fixedMarkupPercent
          ),
          pricing_provider_id: this.getPricingProviderId(),
          article: submitted.article,
          searchKeys: submitted.search_keys,
          stable: submitted.stable
        })
      );
      if (!this.isCurrentProduct(productId)) return;
      Object.entries(submitted).forEach(([key, value]) => {
        const control = this.productForm.controls[key];
        // Keep edits made while the request was in flight ready for the next save.
        if (Object.is(control.value, value)) control.markAsPristine();
      });
      this.notificationService.notice('Товар сохранен!');
      this.product = res;
    } catch (err) {
      if (!this.isCurrentProduct(productId)) return;
      console.error(err);
      this.notificationService.warning('Не удалось сохранить товар. Изменения остались в форме');
    } finally {
      this.saving = false;
    }
  }

  private isCurrentProduct(productId: number): boolean {
    return !this.destroyed && this.product?.id === productId;
  }

  private normalizeFixedMarkupPercent(value: unknown): number | null {
    if (value === undefined || value === null || String(value).trim() === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getPricingProviderId(): number | null {
    const provider = this.productForm.value.pricingProvider as Provider | null | undefined;
    const id = Number(provider?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  async importFromExist() {
    if (!this.product || this.saving) return;
    if (!this.product.article?.trim()) {
      this.notificationService.warning('У товара нет артикула для поиска на Exist');
      return;
    }
    if (this.importingFromExist) return;

    const productId = this.product.id;
    this.importingFromExist = true;
    try {
      await this.runExistImport(productId);
    } catch (err) {
      if (!this.isCurrentProduct(productId)) return;
      console.error(err);
      this.notificationService.warning('Не удалось импортировать товар с Exist');
    } finally {
      this.importingFromExist = false;
    }
  }

  async enrichWithAi() {
    if (!this.product || this.enrichingWithAi) return;
    this.enrichingWithAi = true;
    const productId = this.product.id;
    const ref = this.dialog.open(ProductAiEnrichmentDialogComponent, {
      width: '1180px',
      maxWidth: '96vw',
      disableClose: true,
      data: { productId, productName: this.product.description }
    });
    try {
      const result = await firstValueFrom(ref.afterClosed());
      if (result?.published && this.isCurrentProduct(productId)) {
        await this.loadProduct(productId);
        this.notificationService.notice(`AI применил изменений: ${result.published}`);
      }
    } finally {
      this.enrichingWithAi = false;
    }
  }

  private async runExistImport(
    productId: number,
    selectedExistProductId?: number
  ): Promise<void> {
    if (!this.isCurrentProduct(productId)) return;

    const response = await firstValueFrom(
      this.productService.importFromExist(productId, {
        selected_exist_product_id: selectedExistProductId
      })
    );

    if (!this.isCurrentProduct(productId)) return;

    if (response.status === 'not_found') {
      this.notificationService.warning('На Exist ничего не найдено по этому артикулу');
      return;
    }

    if (response.status === 'needs_selection') {
      const selected = await this.openExistSelectionDialog(response.candidates);
      if (!selected || !this.isCurrentProduct(productId)) return;
      return this.runExistImport(productId, selected.exist_product_id);
    }

    await this.loadProduct(productId);
    this.notificationService.notice(this.buildImportSummary(response));
  }

  private async openExistSelectionDialog(candidates: ProductImportFromExistCandidate[]) {
    if (!this.product) return undefined;

    const dialogRef = this.dialog.open(ExistImportSelectionDialogComponent, {
      width: '900px',
      maxWidth: '96vw',
      data: {
        article: this.product.article,
        candidates
      }
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private buildImportSummary(result: Extract<ProductImportFromExistResult, { status: 'imported' }>) {
    const report = result.report;
    const parts: string[] = [];

    if (report.updated_ua_fields.length) {
      parts.push(`обновлены поля: ${report.updated_ua_fields.join(', ')}`);
    }
    if (report.added_images > 0) {
      parts.push(`добавлено картинок: ${report.added_images}`);
    }
    if (report.created_analog_products > 0 || report.linked_analog_products > 0) {
      parts.push(
        `аналоги: ${report.created_analog_products} созд., ${report.linked_analog_products} связ.`
      );
    }
    if (report.created_oe_products > 0 || report.linked_oe_products > 0) {
      parts.push(`OE: ${report.created_oe_products} созд., ${report.linked_oe_products} связ.`);
    }
    if (report.updated_exist_link) {
      parts.push('ссылка Exist сохранена');
    }
    if (report.skipped_duplicates > 0) {
      parts.push(`дубликаты пропущены: ${report.skipped_duplicates}`);
    }

    return parts.length
      ? `Импорт с Exist завершен: ${parts.join(' · ')}`
      : 'Импорт с Exist завершен без изменений';
  }

  get totalStock(): number {
    return (this.product?.stock || []).reduce((sum, row) => sum + Number(row.quan || 0), 0);
  }

  get providerStockCount(): number {
    return this.supplierGroups.length;
  }

  get searchKeysCount(): number {
    return this.product?.searchKeys?.length || 0;
  }
}
